import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, FileSearch, Plus, Check, Trash2, Building2, Calendar, Link, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const JOB_STATUSES = [
  { id: 'interested', label: 'Intresserad', color: 'bg-gray-100 text-gray-600' },
  { id: 'applied', label: 'Ansökt', color: 'bg-blue-100 text-blue-600' },
  { id: 'interview', label: 'Intervju', color: 'bg-purple-100 text-purple-600' },
  { id: 'offer', label: 'Erbjudande', color: 'bg-green-100 text-green-600' },
  { id: 'rejected', label: 'Avslag', color: 'bg-red-100 text-red-600' },
];

const JOB_SEARCH_TIPS = [
  { title: 'Anpassa CV:t', desc: 'Skräddarsy ditt CV för varje tjänst du söker.' },
  { title: 'Nätverka aktivt', desc: '80% av jobb tillsätts via kontakter. Använd LinkedIn!' },
  { title: 'Förbered intervjun', desc: 'Researcha företaget och öva på vanliga frågor.' },
  { title: 'Följ upp', desc: 'Skicka ett tackmail efter intervjun inom 24 timmar.' },
  { title: 'Håll dig aktiv', desc: 'Sök flera jobb parallellt – lägg inte alla ägg i en korg.' },
  { title: 'Ta hand om dig', desc: 'Jobbsökande är tufft. Vila och fira små framsteg.' },
];

export default function JobSearchPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newApp, setNewApp] = useState({
    company: '',
    position: '',
    url: '',
    appliedDate: '',
    status: 'interested',
    notes: ''
  });

  useEffect(() => {
    if (hasAccess) {
      const saved = localStorage.getItem('job-applications');
      if (saved) {
        setApplications(JSON.parse(saved));
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const saveApplications = (updated) => {
    setApplications(updated);
    localStorage.setItem('job-applications', JSON.stringify(updated));
  };

  const addApplication = () => {
    if (!newApp.company.trim() || !newApp.position.trim()) {
      toast.error('Ange företag och tjänst');
      return;
    }
    const app = {
      id: Date.now(),
      ...newApp,
      appliedDate: newApp.appliedDate || new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString()
    };
    saveApplications([...applications, app]);
    setNewApp({ company: '', position: '', url: '', appliedDate: '', status: 'interested', notes: '' });
    setShowForm(false);
    toast.success('Ansökan tillagd!');
  };

  const updateStatus = (appId, status) => {
    const updated = applications.map(a => a.id === appId ? { ...a, status } : a);
    saveApplications(updated);
    toast.success('Status uppdaterad');
  };

  const deleteApplication = (appId) => {
    saveApplications(applications.filter(a => a.id !== appId));
    toast.success('Ansökan borttagen');
  };

  const getStatusCounts = () => {
    const counts = {};
    JOB_STATUSES.forEach(s => counts[s.id] = 0);
    applications.forEach(a => counts[a.status]++);
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="job-search-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Jobbsökning</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till jobbsökning.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="job-search-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Jobbsökning</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <FileSearch className="w-12 h-12 mx-auto text-teal-500" />
          <h2 className="font-heading text-2xl font-semibold">Jobbsökning</h2>
          <p className="text-muted-foreground">Håll koll på dina ansökningar</p>
        </div>

        {/* Status overview */}
        <div className="flex flex-wrap gap-2 justify-center">
          {JOB_STATUSES.map(status => (
            <div key={status.id} className={`px-3 py-1 rounded-full text-sm ${status.color}`}>
              {status.label}: {statusCounts[status.id]}
            </div>
          ))}
        </div>

        {/* Add button */}
        <Button onClick={() => setShowForm(true)} className="w-full rounded-full" variant="outline">
          <Plus className="w-5 h-5 mr-2" />
          Ny ansökan
        </Button>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Lägg till ansökan</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-muted-foreground">Företag *</label>
                <Input
                  value={newApp.company}
                  onChange={(e) => setNewApp({ ...newApp, company: e.target.value })}
                  placeholder="T.ex. Spotify"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Tjänst *</label>
                <Input
                  value={newApp.position}
                  onChange={(e) => setNewApp({ ...newApp, position: e.target.value })}
                  placeholder="T.ex. UX Designer"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Länk till annons</label>
                <Input
                  value={newApp.url}
                  onChange={(e) => setNewApp({ ...newApp, url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Datum</label>
                <Input
                  type="date"
                  value={newApp.appliedDate}
                  onChange={(e) => setNewApp({ ...newApp, appliedDate: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Status</label>
                <select
                  value={newApp.status}
                  onChange={(e) => setNewApp({ ...newApp, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {JOB_STATUSES.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Anteckningar</label>
                <Input
                  value={newApp.notes}
                  onChange={(e) => setNewApp({ ...newApp, notes: e.target.value })}
                  placeholder="Kontaktperson, lön, etc."
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                Avbryt
              </Button>
              <Button onClick={addApplication} className="rounded-full">
                Lägg till
              </Button>
            </div>
          </div>
        )}

        {/* Applications list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-teal-200 border-t-teal-500 rounded-full animate-spin" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Inga ansökningar än. Lägg till din första!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {applications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(app => {
              const status = JOB_STATUSES.find(s => s.id === app.status);
              return (
                <div key={app.id} className="bg-white p-4 rounded-xl border border-border/30">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{app.position}</h4>
                          <p className="text-sm text-muted-foreground">{app.company}</p>
                        </div>
                        <button
                          onClick={() => deleteApplication(app.id)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${status.color}`}>
                          {status.label}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(app.appliedDate).toLocaleDateString('sv-SE')}
                        </span>
                        {app.url && (
                          <a 
                            href={app.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-teal-600 flex items-center gap-1 hover:underline"
                          >
                            <Link className="w-3 h-3" />
                            Annons
                          </a>
                        )}
                      </div>
                      {/* Status update buttons */}
                      <div className="flex flex-wrap gap-1 mt-3">
                        {JOB_STATUSES.map(s => (
                          <button
                            key={s.id}
                            onClick={() => updateStatus(app.id, s.id)}
                            className={`px-2 py-0.5 rounded text-xs transition-all ${
                              app.status === s.id ? s.color + ' font-semibold' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
          <h3 className="font-semibold text-teal-800 mb-3">Tips för jobbsökande</h3>
          <div className="grid gap-2 md:grid-cols-2">
            {JOB_SEARCH_TIPS.map((tip, i) => (
              <div key={i} className="text-sm">
                <span className="font-medium text-teal-700">{tip.title}:</span>{' '}
                <span className="text-teal-600">{tip.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
